/**
 * Gestione centralizzata dell'allarme sonoro/vibrazione di una selettiva
 * in arrivo. Replica legacy: la sirena suona in LOOP finche' l'utente
 * non risponde (Accetto / Non Accetto). La vibrazione e' in interval
 * continuo se l'utente ha "bypassVolume" attivo.
 *
 * AUDIO OWNERSHIP UNIFICATA (P4 problemi strutturali):
 * Quando giriamo dentro il wrapper RN (isInNativeWrapper=true) deleghiamo
 * sempre al wrapper via bridge — il wrapper suona i suoni nativi bundled.
 * In browser puro i suoni sono SEMPRE built-in, generati via Web Audio API
 * (niente audio personalizzati: rimossi per decisione 2026-06-10).
 * Niente piu' guard sparsi in GlobalSelectiveAlert ne' rischio di doppio audio.
 */

import {
  playNativeSelectiveAlarm, stopNativeSelectiveAlarm,
  playNativeEndChime, stopNativeEndChime,
} from './nativeBridge';

let vibrationInterval: number | null = null;
let endChimeInterval: number | null = null;

export const selectiveAlarm = {
  /**
   * Avvia sirena in loop + vibrazione.
   * Browser puro: sirena built-in generata via Web Audio API.
   * @param bypassVolume se true, attiva anche vibrazione continua
   */
  start(bypassVolume = false): void {
    this.stop(); // ferma eventuale allarme precedente

    // Wrapper RN: delega via bridge (suoni nativi, no doppia sirena).
    if (playNativeSelectiveAlarm()) return;

    // Browser puro: sirena built-in (beep loop Web Audio)
    startBeepLoop();

    // Prima vibrazione subito
    if ('vibrate' in navigator) {
      navigator.vibrate([500, 200, 500, 200, 500]);
    }

    // Vibrazione continua (per chi ha bypassVolume attivo)
    if (bypassVolume && 'vibrate' in navigator) {
      vibrationInterval = window.setInterval(() => {
        navigator.vibrate([500, 200]);
      }, 700);
    }
  },

  stop(): void {
    // Wrapper RN: delega
    stopNativeSelectiveAlarm();

    if (vibrationInterval !== null) {
      clearInterval(vibrationInterval);
      vibrationInterval = null;
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(0);
    }
    stopBeepLoop();
  },

  isPlaying(): boolean {
    return beepCtx !== null;
  },

  /**
   * Suono di fine selettiva in LOOP (Bryan: continua finche' l'utente conferma).
   * Browser puro: 3 beep ascendenti built-in generati via Web Audio API.
   * Stop con stopEndChime().
   */
  playEndChime(): void {
    this.stopEndChime();
    // Wrapper RN: delega via bridge.
    if (playNativeEndChime()) return;
    // Browser puro: beep ascendenti built-in
    startEndChimeBeepLoop();
    if ('vibrate' in navigator) navigator.vibrate([100, 80, 100, 80, 250]);
  },

  stopEndChime(): void {
    stopNativeEndChime();
    if (endChimeInterval !== null) {
      clearInterval(endChimeInterval);
      endChimeInterval = null;
    }
  },
};

function startEndChimeBeepLoop(): void {
  const fire = () => {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [{ f: 523, t: 0 }, { f: 659, t: 0.25 }, { f: 784, t: 0.5 }].forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(f, now + t);
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.4, now + t + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.45);
      });
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1500);
    } catch { /* ignore */ }
  };
  fire();
  endChimeInterval = window.setInterval(fire, 1500);
}

// === Sirena built-in (beep loop Web Audio) ===
let beepCtx: AudioContext | null = null;
let beepInterval: number | null = null;

function startBeepLoop(): void {
  stopBeepLoop();
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    beepCtx = new Ctx();
    const fire = () => {
      if (!beepCtx) return;
      const now = beepCtx.currentTime;
      [0, 0.4, 0.8].forEach(delay => {
        const osc = beepCtx!.createOscillator();
        const gain = beepCtx!.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(880, now + delay);
        osc.frequency.setValueAtTime(440, now + delay + 0.15);
        gain.gain.setValueAtTime(0.3, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.3);
        osc.connect(gain).connect(beepCtx!.destination);
        osc.start(now + delay);
        osc.stop(now + delay + 0.35);
      });
    };
    fire();
    beepInterval = window.setInterval(fire, 1500);
  } catch {
    // niente audio possibile
  }
}

function stopBeepLoop(): void {
  if (beepInterval !== null) {
    clearInterval(beepInterval);
    beepInterval = null;
  }
  if (beepCtx) {
    try { beepCtx.close(); } catch { /* ignore */ }
    beepCtx = null;
  }
}
